import { ChangeDetectorRef, Component,  EventEmitter,  Input, OnInit, Output } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { CalendarDays, DayFullAbbr } from 'src/app/helpers/constants';
import { JoiEvent, Label } from 'globals/store/events.data';
import { FilterGroupTypes } from 'src/app/store/filters.era';
import { CalendarDay, CalendarEvent, getCalendarWeeks } from 'src/app/store/reselectors/calendars.reselector';
import { accountEventLabelMapsReselector, filteredCalendarEventsReselectorGenerator } from 'src/app/store/reselectors/filteredEvents.reselector';
import { calendarActions } from './../../store/calendars.era';
import { EventViewService } from 'src/app/services/event.view.service';
import { reselectorsByCalendarTaskType } from 'src/app/store/reselectors/filteredTasks.reselector';
import { Task } from 'globals/store/tasks.data';
import { TaskViewService } from './../../services/task.view.service';
import { eventTaskLabelMapsReselector } from 'src/app/store/reselectors/events.reselector';
import { User } from 'globals/store/user.data';
import { isGuestReselector, readOnlyReselector } from 'src/app/store/reselectors/permissions.reselector';
import { Wrapper } from 'src/app/components/wrapper.component';
import { date2str, str2Date } from 'globals/helpers/helpers';
import { IconComponent } from 'src/app/components/icon/icon.component';
import { TaskToggleMenuComponent } from 'src/app/components/task-toggle-menu/task-toggle-menu.component';
import { CommonModule } from '@angular/common';
import { LabelDotsComponent } from './label-dots.component';
import { EventStatusComponent } from 'src/app/components/events/event-status.component';
import { BlackBarWrapperComponent } from 'src/app/components/wrappers/black-bar-wrapper.component';

@Component({
    selector: 'calendar',
    templateUrl: './calendar.html',
    styleUrls: ['./calendar.scss'],
    standalone: true,
    imports: [IconComponent, TaskToggleMenuComponent, CommonModule, BlackBarWrapperComponent, LabelDotsComponent, EventStatusComponent],
})

export class CalendarComponent extends Wrapper<CalendarComponent> implements OnInit {
  @Input() filterPage: keyof FilterGroupTypes = 'allTasksCalendar'
  @Input() isSingleEvent = false
  @Output() addDatedTask: EventEmitter<{ dateId: string, event: Event }> = new EventEmitter();

  todayId = date2str(new Date())
  currentDate = new Date()
  selectedDateId
  selectedDate
  weekDays = CalendarDays
  dayAbbr = DayFullAbbr
  event: JoiEvent
  events: Dictionary<JoiEvent> = {}
  tasks: Dictionary<Task> = {}
  user: User

  eventDateIds: Set<string> = new Set()
  calendarEventsByDate: Dictionary<CalendarEvent[]> = {}
  tasksByDate: TasksByDate = {}
  accountEventLabelMaps: { [accountId: string]: { [labelId: string]: Label } } = {}
  eventTasksLabelMaps: { [eventId: string]: Dictionary<Label> } = {}
  taskLabels: Dictionary<Label> = {}
  hasTasks: boolean
  weeks: CalendarDay[][]
  readOnly = false
  isGuest = false

  constructor(
    private eventViewService: EventViewService,
    private taskViewService: TaskViewService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    super()
    this.subProp(this.store.select('user', 'user'), 'user')
  }

  ngOnInit() {
    if (this.isSingleEvent) this.selectProp(readOnlyReselector, null, 'readOnly')
    if (this.isSingleEvent) this.selectProp(isGuestReselector, null, 'isGuest')
    this.hasTasks = this.filterPage !== 'home'
    // console.log({ single: this.isSingleEvent, readOnly: this.readOnly, type: this.filterPage, hasTasks: this.hasTasks })

    if (!this.isSingleEvent) this.subCallback(this.store.select(filteredCalendarEventsReselectorGenerator(this.filterPage)), ({ calendarEventsByDate }) => {
      // console.log({ calendarEventsByDate })
      this.calendarEventsByDate = calendarEventsByDate
    })
    this.subCallback(this.store.select('calendars', 'calendarDates'), calendarDates => {
      const selectedDate = calendarDates[this.filterPage] || this.currentDate
      // console.log({ calendarDates, selectedDate })
      this.weeks = getCalendarWeeks(selectedDate)
      this.selectedDateId = date2str(selectedDate)
      this.selectedDate = selectedDate
      // console.log({ calendarType: this.calendarType, weeks: this.weeks, dateId: this.selectedDateId, selectedDate })
    })

    if (this.hasTasks) this.subProp(this.store.select(reselectorsByCalendarTaskType[this.filterPage]), 'tasksByDate')

    this.subCallback(this.store.select('events', 'event'), event => {
      this.event = event
      this.eventDateIds = new Set(this.filterPage === 'tasksCalendar' && event ? event.dates || [] : [])
    })

    if (this.hasTasks) this.subProp(this.store.select('tasks', 'entities'), 'tasks')
    if (this.filterPage === 'allTasksCalendar') this.subProp(this.store.select(eventTaskLabelMapsReselector), 'eventTasksLabelMaps')

    if (this.isSingleEvent) {
      this.subProp(this.store.select('events', 'labelMaps', 'task_labels'), 'taskLabels')
      return
    }
    this.subProp(this.store.select('events', 'entities'), 'events')
    this.subProp(this.store.select(accountEventLabelMapsReselector), 'accountEventLabelMaps')

  }

  async viewEvent(event: JoiEvent) {
    // console.log({ event })
    if (!event) return
    this.eventViewService.openViewEventDialog(event, this.dialog, this.isSingleEvent)
    // const temp_evt = this.getEventById(evt.eventId);
  }

  pause() {
    this.changeDetectorRef.detach()
  }

  unpause() {
    this.changeDetectorRef.reattach()
  }

  async manageTask(task: Task) {
    this.pause()
    await this.taskViewService.manageTaskThroughDialog(this.dialog, task, task.eventId, !task.name, this.changeDetectorRef, this.filterPage)
    this.unpause()
  }

  async addTask(dateId: string, event: Event) {
    this.pause()
    // console.log({ dateId, event, single: this.isSingleEvent })
    if (this.isSingleEvent) {
      const eventId = this.event.eventId
      const task = await this.taskService.createAndSaveTask('', eventId, dateId)
      await this.taskViewService.manageTaskThroughDialog(this.dialog, task, eventId, true, this.changeDetectorRef, this.filterPage)
    } else await this.addDatedTask.emit({ event, dateId })
    this.unpause()
  }

  toggleTaskCompleted(event: MouseEvent, task: Task) {
    event.stopPropagation()
    this.taskService.toggleTaskCompleted(task)
  }

  moveMonths(nMonths: number) {
    const date = this.selectedDate
    date.setMonth(date.getMonth() + nMonths)
    this.store.dispatch(calendarActions.updateCalendarDate({ date, calendarType: this.filterPage }))
  }

  backToday() {
    this.store.dispatch(calendarActions.updateCalendarDate({ date: this.currentDate, calendarType: this.filterPage }))
  }

  setDate(dateId: string) {
    this.store.dispatch(calendarActions.updateCalendarDate({ date: str2Date(dateId), calendarType: this.filterPage }))
  }

  trackById(index, item) {
    return item.id
  }

  trackByCalendarEvent(index, event: CalendarEvent) {
    return event ? event.eventId : index
  }

  trackByIndex(index) {
    return index
  }
  
}

export interface LabelsByEvent { [eventId: string]: { [labelId: string]: Label } }
export interface TasksByDate { [dateId: string]: Task[] }